"use client";

import { motion } from "framer-motion";
import { Banknote, Compass, Share2 } from "lucide-react";

import { Container } from "@/app/(marketing)/_components/ui/container";

interface Feature {
  icon: typeof Compass;
  eyebrow: string;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: Compass,
    eyebrow: "Source",
    title: "Every open search, not just the ones in your niche",
    description:
      "Most platforms only show you roles that match your own background. Terac shows you the whole demand board. Source across industries, countries, and specialties, wherever your network actually reaches.",
  },
  {
    icon: Share2,
    eyebrow: "Submit",
    title: "One candidate, one link, no back office",
    description:
      "No applications to fill out, no resumes to format, no interviews to run. Submit your candidate with a single link and our AI Moderator screens, verifies, and qualifies them for you.",
  },
  {
    icon: Banknote,
    eyebrow: "Get paid",
    title: "Commission lands the second a placement clears",
    description:
      "20% deposits straight to your account the moment a candidate you submitted is approved. No invoicing, no net-30, no payout request. It stacks across every study they clear, up to a $1,000 cap per person.",
  },
];

export function RecruitersAISupercharge() {
  return (
    <section className="py-20 md:py-28">
      <Container>
        <div className="mb-14 flex flex-col items-center gap-4">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              Recruiting partner, supercharged
            </p>
          </div>
          <h2 className="text-marketing-text dark:text-marketing-cream max-w-[760px] text-center text-3xl leading-[1.1] font-medium tracking-[-1.5px] md:text-4xl lg:text-[50px]">
            You bring the candidate. We handle everything else.
          </h2>
          <p className="text-marketing-text-muted max-w-[560px] text-center text-base">
            No interviews to run, no resumes to format, no invoices to chase. We
            screen and verify every candidate you submit, so the only thing you
            own is the part that&apos;s actually hard: knowing the right person.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {features.map((feature, index) => (
            <motion.div
              key={feature.eyebrow}
              initial={{ opacity: 0, transform: "translateY(16px)" }}
              whileInView={{ opacity: 1, transform: "translateY(0px)" }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.08 }}
              className="border-marketing-border bg-marketing-cream-light flex flex-col rounded-2xl border px-6 pt-6 pb-7"
            >
              <div className="mb-5 flex items-center gap-2.5">
                <div className="bg-marketing-card-blue/50 flex h-10 w-10 items-center justify-center rounded-xl">
                  <feature.icon className="text-marketing-blue h-5 w-5" />
                </div>
                <span className="text-marketing-blue font-mono text-[12px] tracking-[-0.2px] uppercase">
                  {feature.eyebrow}
                </span>
              </div>
              <h3 className="text-marketing-text dark:text-marketing-cream mb-2 text-[18px] leading-snug font-semibold tracking-tight">
                {feature.title}
              </h3>
              <p className="text-marketing-text-muted text-[14px] leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
